import React, { useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { cx } from "../../Util/classHelpers";
import {
    useFetchChDurationDrilldownQuery,
    useFetchChLatenessDrilldownQuery,
    useLazyFetchChDurationDrilldownQuery,
    useLazyFetchChLatenessDrilldownQuery,
} from "../../Apis/chPerformanceApi";
import {
    CHART_TYPES,
    TIMEFRAME_OPTIONS_MAP,
    mapBucketIndexToEnum,
    getBucketLabel,
} from "../../ChPerformanceReportHelperLib/chPerformanceDataHelper";
import { ChCalendarIcon, DownloadIcon } from "../Icons/ch-analytics-icons";
import ChPerformanceSingleChart from "./ChPerformanceSingleChart";
import OrderDetailModal from "../Orders/OrderDetailModal";
import { exportToCsv } from "../../Util/csvExport";
import { useOnTimeBufferMinutes } from "../../Hooks/useOnTimeBufferMinutes";
import styles from "./chPerformanceReport.module.css";

const CHART_TITLES = {
    [CHART_TYPES.PICKUP]: "Placement to Pick up Time",
    [CHART_TYPES.DELIVERY]: "Placement to Delivery Time",
    [CHART_TYPES.DRIVE]: "Drive time (pickup to delivery)",
    [CHART_TYPES.LATENESS]: "On-time Deliveries",
};

const PAGE_SIZE = 25;

const ChPerformanceDrilldownView = ({
    chartType,
    bucketIndex,
    page,
    companyCharts,
    companySummaries,
    selectedCompanyIds,
    companyIdsCsv,
    apiTimeframe,
    timeframe,
    startDate,
    endDate,
    dateRange,
    accountsMap,
    onBarClick,
    onClose,
    onPageChange,
}) => {
    const contentKey = `${chartType}-${bucketIndex}-${apiTimeframe}`;
    const onTimeBufferMinutes = useOnTimeBufferMinutes();

    return (
        <div className={styles.drilldownLayout}>
            <div>
                <ChPerformanceSingleChart
                    type={chartType}
                    title={CHART_TITLES[chartType] ?? chartType}
                    companyCharts={companyCharts}
                    companySummaries={companySummaries}
                    selectedCompanyIds={selectedCompanyIds}
                    onBarClick={onBarClick}
                    highlightedBucketIndex={bucketIndex}
                    onTimeBufferMinutes={onTimeBufferMinutes}
                />
            </div>
            <DrilldownPanel
                key={contentKey}
                chartType={chartType}
                bucketIndex={bucketIndex}
                page={page}
                companyIdsCsv={companyIdsCsv}
                apiTimeframe={apiTimeframe}
                timeframe={timeframe}
                startDate={startDate}
                endDate={endDate}
                dateRange={dateRange}
                accountsMap={accountsMap}
                onClose={onClose}
                onPageChange={onPageChange}
            />
        </div>
    );
};

const DrilldownPanel = ({
    chartType,
    bucketIndex,
    page,
    companyIdsCsv,
    apiTimeframe,
    timeframe,
    startDate,
    endDate,
    dateRange,
    accountsMap,
    onClose,
    onPageChange,
}) => {
    const isDuration = chartType !== CHART_TYPES.LATENESS;
    const bucketEnum = mapBucketIndexToEnum(chartType, bucketIndex);

    // Enterprise on-time grace buffer, applied consistently to the On-time badge,
    // the CSV export, and the lateness bucket labels. The lateness chart and its
    // backend buckets are buffered too, so the drilldown never contradicts the bar.
    const onTimeBufferMinutes = useOnTimeBufferMinutes();
    const bucketLabel = getBucketLabel(chartType, bucketIndex, onTimeBufferMinutes);
    const tfLabel = timeframe === "range" && dateRange?.start && dateRange?.end
        ? `${fmtRangeDate(dateRange.start)} – ${fmtRangeDate(dateRange.end)}`
        : TIMEFRAME_OPTIONS_MAP[timeframe] ?? timeframe;
    const chartTitle = CHART_TITLES[chartType] ?? chartType;

    const durationResult = useFetchChDurationDrilldownQuery(
        { companyIds: companyIdsCsv, timeframe: apiTimeframe, type: chartType?.toUpperCase(), bucket: bucketEnum, startDate, endDate, page, pageSize: PAGE_SIZE },
        { skip: !isDuration || !bucketEnum }
    );
    const latenessResult = useFetchChLatenessDrilldownQuery(
        { companyIds: companyIdsCsv, timeframe: apiTimeframe, bucket: bucketEnum, startDate, endDate, page, pageSize: PAGE_SIZE, onTimeBufferMinutes },
        { skip: isDuration || !bucketEnum }
    );

    const { data, isFetching } = isDuration ? durationResult : latenessResult;
    const orders = data?.orders ?? [];
    const totalCount = data?.totalCount ?? 0;
    const totalPages = data?.totalPages ?? 0;
    const companiesWithOrders = data?.companiesWithOrders ?? 0;

    const [selectedOrder, setSelectedOrder] = useState({ orderId: null, companyId: null });
    const [isExporting, setIsExporting] = useState(false);
    const [truncatedWarning, setTruncatedWarning] = useState(false);

    const handleOrderClick = useCallback((orderId, companyId) => {
        setSelectedOrder({ orderId, companyId });
    }, []);

    const handleOrderModalClose = useCallback(() => {
        setSelectedOrder({ orderId: null, companyId: null });
    }, []);

    const [triggerDurationExport] = useLazyFetchChDurationDrilldownQuery();
    const [triggerLatenessExport] = useLazyFetchChLatenessDrilldownQuery();

    const handleExport = useCallback(async () => {
        setIsExporting(true);
        setTruncatedWarning(false);
        try {
            const today = new Date().toISOString().slice(0, 10);
            const typeSlug = chartType?.toLowerCase().replace(/_/g, "-") ?? "performance";
            const bucketSlug = bucketLabel?.toLowerCase().replace(/\s+/g, "-") ?? "bucket";
            const filename = `${typeSlug}_${bucketSlug}_${timeframe}_${today}.csv`;
            const trigger = isDuration ? triggerDurationExport : triggerLatenessExport;
            const args = isDuration
                ? { companyIds: companyIdsCsv, timeframe: apiTimeframe, type: chartType?.toUpperCase(), bucket: bucketEnum, startDate, endDate, exportAll: true }
                : { companyIds: companyIdsCsv, timeframe: apiTimeframe, bucket: bucketEnum, startDate, endDate, exportAll: true, onTimeBufferMinutes };
            const result = await trigger(args).unwrap();
            exportToCsv(COLUMNS, result.orders, filename, { accountsMap, onTimeBufferMinutes });
            if (result.truncated) setTruncatedWarning(true);
        } finally {
            setIsExporting(false);
        }
    }, [isDuration, triggerDurationExport, triggerLatenessExport, companyIdsCsv, apiTimeframe, chartType, bucketEnum, bucketLabel, timeframe, startDate, endDate, accountsMap, onTimeBufferMinutes]);

    useEffect(() => {
        function onKey(e) {
            if (e.key === "Escape" && !selectedOrder.orderId) onClose();
        }
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose, selectedOrder.orderId]);

    return (
        <div className={styles.drilldownPanel}>
            <div className={styles.drilldownHeader}>
                <div className={styles.drilldownTitleRow}>
                    <div className={styles.drilldownTitle}>{chartTitle}</div>
                    <button
                        className={styles.exportBtn}
                        onClick={handleExport}
                        disabled={isExporting}
                        title="Export as CSV"
                    >
                        <DownloadIcon size={13} />
                        {isExporting ? "Exporting…" : "Export"}
                    </button>
                    <button className={styles.drilldownCloseBtn} onClick={onClose}>{"✕"}</button>
                </div>
                <div className={styles.drilldownScope}>
                    <span className={styles.drilldownScopeTag}>{bucketLabel}</span>
                    <span className={styles.drilldownScopeTag}>
                        <ChCalendarIcon />
                        {tfLabel}
                    </span>
                </div>
                <div className={styles.drilldownStats}>
                    <div className={styles.drilldownStat}>
                        <div className={styles.drilldownStatVal}>{isFetching ? "—" : totalCount.toLocaleString()}</div>
                        <div className={styles.drilldownStatLbl}>Orders in bucket</div>
                    </div>
                    <div className={styles.drilldownStat}>
                        <div className={styles.drilldownStatVal}>{isFetching ? "—" : companiesWithOrders}</div>
                        <div className={styles.drilldownStatLbl}>Companies</div>
                    </div>
                </div>
            </div>

            {truncatedWarning && (
                <div className={styles.truncatedBanner}>
                    {"Export limited to 10,000 rows. Refine your filters to get complete data."}
                    <button className={styles.truncatedDismiss} onClick={() => setTruncatedWarning(false)}>{"✕"}</button>
                </div>
            )}

            <div className={styles.drilldownBody}>
                <DrilldownTable orders={orders} accountsMap={accountsMap} loading={isFetching} onOrderClick={handleOrderClick} onTimeBufferMinutes={onTimeBufferMinutes} />
            </div>

            <div className={styles.drilldownFooter}>
                <DrilldownPagination
                    page={page}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    pageSize={PAGE_SIZE}
                    loading={isFetching}
                    onPageChange={onPageChange}
                />
            </div>
            {ReactDOM.createPortal(
                <OrderDetailModal
                    orderId={selectedOrder.orderId}
                    companyId={selectedOrder.companyId}
                    open={!!selectedOrder.orderId && !!selectedOrder.companyId}
                    onClose={handleOrderModalClose}
                />,
                document.body
            )}
        </div>
    );
};

const COLUMNS = [
    { key: "orderNumber",     header: "Order #",      type: "orderlink" },
    { key: "companyId",       header: "Location",     type: "company" },
    { key: "customerName",    header: "Customer" },
    { key: "customerAddress", header: "Address" },
    { key: "placementTime",   header: "Placed at",    type: "datetime" },
    { key: "pickupTime",      header: "Pickup at",    type: "datetime" },
    { key: "deliveryTime",    header: "Delivered at", type: "datetime" },
    { key: "promisedDeliveryTime", header: "On-time", type: "ontime" },
    { key: "totalCost",       header: "Amount",       type: "currency" },
];

const DrilldownTable = ({ orders, accountsMap, loading, onOrderClick, onTimeBufferMinutes }) => {
    return (
        <table className={styles.drilldownTable}>
            <thead>
                <tr>
                    {COLUMNS.map((col) => (
                        <th key={col.key}>{col.header}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i}>
                            {COLUMNS.map((col) => (
                                <td key={col.key}><div className={styles.drilldownSkelRow} /></td>
                            ))}
                        </tr>
                    ))
                ) : orders.length === 0 ? (
                    <tr>
                        <td colSpan={COLUMNS.length} className={styles.drilldownEmpty}>No orders found</td>
                    </tr>
                ) : (
                    orders.map((order, i) => (
                        <tr key={order.orderId ?? order.orderNumber ?? i}>
                            {COLUMNS.map((col) => (
                                <td key={col.key} title={col.type !== "orderlink" ? String(order[col.key] ?? "") : undefined}>
                                    <CellValue col={col} order={order} accountsMap={accountsMap} onOrderClick={onOrderClick} onTimeBufferMinutes={onTimeBufferMinutes} />
                                </td>
                            ))}
                        </tr>
                    ))
                )}
            </tbody>
        </table>
    );
};

const CellValue = ({ col, order, accountsMap, onOrderClick, onTimeBufferMinutes = 0 }) => {
    const raw = order[col.key];

    switch (col.type) {
        case "orderlink":
            return (
                <button
                    className={styles.drilldownOrderLink}
                    onClick={() => onOrderClick(order.orderId, order.companyId)}
                >
                    {raw || "—"}
                </button>
            );
        case "ontime": {
            const promised = order.promisedDeliveryTime;
            const delivered = order.deliveryTime;
            if (!promised || !delivered) return <span>{"—"}</span>;
            // Truncate toward zero to match the backend's integer-minute lateness
            const diffMins = Math.trunc(
                (new Date(delivered.replace(" ", "T")) - new Date(promised.replace(" ", "T"))) / 60000
            );
            // diffMins > 0 => late, < 0 => early, 0 => exactly on time.
            if (diffMins < 0) {
                return <span className={cx(styles.drilldownBadge, styles.drilldownBadgeOntime)}>{Math.abs(diffMins)}{" min early"}</span>;
            }
            if (diffMins <= onTimeBufferMinutes) {
                // On time, or late but within the grace buffer.
                return <span className={cx(styles.drilldownBadge, styles.drilldownBadgeOntime)}>{"On-time"}</span>;
            }
            return <span className={cx(styles.drilldownBadge, styles.drilldownBadgeLate)}>{diffMins}{" min late"}</span>;
        }
        case "datetime":
            return <span className={styles.drilldownDtVal}>{formatDatetime(raw)}</span>;
        case "company":
            return <span>{accountsMap?.[raw]?.companyName ?? raw ?? "—"}</span>;
        case "currency": {
            const val = Number(raw);
            return <span>{isNaN(val) ? "—" : `$${val.toFixed(2)}`}</span>;
        }
        default:
            return <span>{raw || "—"}</span>;
    }
};

const DrilldownPagination = ({ page, totalPages, totalCount, pageSize, loading, onPageChange }) => {
    if (!totalPages) return null;

    const from = (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, totalCount);
    const pages = buildPageRange(page, totalPages);

    return (
        <div className={styles.drilldownPagination}>
            <span className={styles.drilldownPgInfo}>
                {loading ? "Loading…" : `Showing ${from}–${to} of ${totalCount.toLocaleString()}`}
            </span>
            <div className={styles.drilldownPgBtns}>
                <button
                    className={styles.drilldownPgBtn}
                    disabled={page <= 1 || loading}
                    onClick={() => onPageChange(page - 1)}
                >
                    {"‹"}
                </button>
                {pages.map((p, i) =>
                    p === "…" ? (
                        <span key={`ellipsis-${i}`} className={styles.drilldownPgEllipsis}>{"…"}</span>
                    ) : (
                        <button
                            key={p}
                            className={cx(styles.drilldownPgBtn, p === page && styles.drilldownPgCur)}
                            disabled={loading}
                            onClick={() => p !== page && onPageChange(p)}
                        >
                            {p}
                        </button>
                    )
                )}
                <button
                    className={styles.drilldownPgBtn}
                    disabled={page >= totalPages || loading}
                    onClick={() => onPageChange(page + 1)}
                >
                    {"›"}
                </button>
            </div>
        </div>
    );
};

function buildPageRange(current, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 4) return [1, 2, 3, 4, 5, "…", total];
    if (current >= total - 3) return [1, "…", total - 4, total - 3, total - 2, total - 1, total];
    return [1, "…", current - 1, current, current + 1, "…", total];
}

function fmtRangeDate(dateStr) {
    return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
    });
}

function formatDatetime(val) {
    if (!val) return "—";
    try {
        const d = new Date(val.replace(" ", "T"));
        if (isNaN(d)) return val;
        return d.toLocaleString("en-US", {
            month: "short", day: "numeric",
            hour: "numeric", minute: "2-digit", hour12: true,
        });
    } catch {
        return val;
    }
}

export default ChPerformanceDrilldownView;
